import React from "react";

/**
 * "Virtualized Workstation Experience" — three screens sit side by side in the
 * artwork: an engineering station running ladder logic, an HMI/SCADA mimic, and
 * a historian/database view. Like the other cyber-range illustrations the Figma
 * export is one flat bitmap, so nothing in it can move on its own; this layer is
 * drawn on top of `virtualized-workstation.png` (1600x344) and gives each screen
 * the motion its own tool would have.
 *
 * Every coordinate below was measured off that bitmap — rails, contacts, coils,
 * the SCADA bus and the two chart traces were read straight out of the pixels —
 * so the moving parts land on the artwork's own linework rather than beside it.
 */

type Vars = React.CSSProperties & Record<string, string | number>;

/* ------------------------------------------------------------------ screen 1
 * Engineering station: a PLC scan. Each rung is one path that starts at the
 * left power rail and stops where the artwork's symbols break the wire, so a
 * packet running the path reads as current hopping the contacts on its way to
 * the coil. `len` is the summed length of the path's segments; the rungs fire
 * in program order and the scan restarts once the last one lands.
 */
type Rung = { d: string; len: number; delay: number };

const RUNGS: Rung[] = [
  // Rung 1, top branch: contact at x181..194, coil at x350..364.
  { d: "M150 98.5 H181 M194 98.5 H350 M364 98.5 H405", len: 228, delay: 0 },
  // Rung 1, parallel branch: rejoins the top branch at x225.5 and drives the
  // same coil, which is why it runs all the way through instead of stopping.
  { d: "M150 134.5 H181 M194 134.5 H225.5 V98.5 H350 M364 98.5 H405", len: 264, delay: 0.1 },
  // Rung 2: normally-closed contact, a second contact at x258..270, then a coil.
  { d: "M150 188 H181 M194 188 H258 M270 188 H350 M364 188 H405", len: 216, delay: 1.45 },
  // Rung 3: the wire disappears behind the two function blocks.
  { d: "M150 240.5 H181 M194 240.5 H283 M334 240.5 H379 M430 240.5 H456", len: 191, delay: 2.95 },
];

/**
 * Symbols lighting up as the scan reaches them. A rung's packet covers its path
 * over 17% of the cycle, so a symbol `s` units along a rung of length `len`
 * fires at `delay + 0.17 * CYCLE * (s / len)` — that is where these times come
 * from, and it is what keeps the flash on the wavefront rather than near it.
 */
/** Contacts: the spark bridges the symbol's two bars, i.e. the contact closes. */
const CONTACTS = [
  { x0: 181, x1: 194, y: 98.5, t: 0.13 },
  { x0: 181, x1: 194, y: 134.5, t: 0.21 },
  { x0: 181, x1: 194, y: 188, t: 1.58 },
  { x0: 258, x1: 270, y: 188, t: 1.85 },
  { x0: 181, x1: 194, y: 240.5, t: 3.1 },
];
/** Coils energising, drawn as a ring over the artwork's own "( )". */
const COILS = [
  { cx: 357, cy: 98.5, t: 0.75 },
  { cx: 357, cy: 188, t: 2.19 },
];
/** Function blocks on rung 3, outlined as the rung executes them. */
const BLOCKS = [
  { x: 283, y: 224, w: 52, h: 33, t: 3.53 },
  { x: 379, y: 224, w: 52, h: 33, t: 3.74 },
];
/**
 * The cyan tag labels above each symbol. They flash with the symbol they name,
 * so the label set reads top-to-bottom in program order like a live cross
 * reference rather than as decoration.
 */
const TAGS = [
  { x: 171, y: 80, w: 34, h: 7, t: 0.13 },
  { x: 341, y: 80, w: 34, h: 7, t: 0.75 },
  { x: 171, y: 116, w: 34, h: 6, t: 0.21 },
  { x: 171, y: 168, w: 34, h: 6, t: 1.58 },
  { x: 247, y: 168, w: 36, h: 6, t: 1.85 },
  { x: 341, y: 168, w: 34, h: 6, t: 2.19 },
  { x: 171, y: 220, w: 34, h: 7, t: 3.1 },
];

/* ------------------------------------------------------------------ screen 2
 * HMI/SCADA: the cyan trunk carries a poll from the top-left device, along the
 * bus and down each drop to the tag groups; the red links then answer with the
 * values, one row at a time.
 */
const HMI_ROUTES: Rung[] = [
  { d: "M705 126 V252", len: 126, delay: 0 },
  { d: "M705 148.5 H896", len: 191, delay: 0.15 },
  { d: "M896 126 V252", len: 126, delay: 0.6 },
  { d: "M833.5 148.5 V252", len: 103.5, delay: 0.85 },
];

/** Value rows: the red junction dots and the short links that feed each box. */
const HMI_ROWS = [
  {
    t: 1.3,
    dots: [
      [723.5, 187],
      [814, 187],
      [915, 187],
    ],
    links: ["M706 192.5 H723", "M814 193 H849", "M896 192.5 H915"],
  },
  {
    t: 1.75,
    dots: [
      [723.5, 216.5],
      [814, 216.5],
      [915, 216.5],
    ],
    links: ["M706 221.5 H723", "M814 221.5 H834", "M896 221.5 H915"],
  },
  {
    t: 2.2,
    dots: [
      [723.5, 246],
      [814, 246],
      [915, 246],
    ],
    links: ["M686 251.5 H723", "M814 251.5 H834", "M896 251.5 H915"],
  },
] as const;

/** The alarm banner top-left: two bars taking turns, the way an active alarm reads. */
const ALARM_BARS = [
  { x: 644, y: 89, w: 26, h: 11, delay: "0s" },
  { x: 644, y: 102, w: 26, h: 11, delay: "-0.85s" },
];

/* ------------------------------------------------------------------ screen 3
 * Historian: both traces are re-drawn left to right on the artwork's own
 * polylines — measured column by column off the bitmap — with a head marker
 * riding the same path, so the chart reads as replaying its history.
 */
const CYAN_TRACE =
  "M1250 212.5 L1269 205.5 L1294 217.5 L1326 183.5 L1354 204.5 L1388 179.5 L1430 205.5 L1445 183.5";
const CYAN_TRACE_LEN = 248;
/** The red trace runs down to the chart floor, resets to the top, and falls again. */
const RED_TRACE = "M1250 172.3 L1379 263.2 L1379 142.6 L1446 176";
const RED_TRACE_LEN = 353;

/** Record rows on the left of the historian, filling top to bottom. */
const RECORD_ROWS = [88, 108, 129, 149, 170, 190, 211, 232, 253];

/**
 * A packet is drawn twice off one dash pattern — a wide dim pass for glow and a
 * bright core — so the two can never drift apart. The comet is a fixed share of
 * the route, clamped so short drops aren't swamped by their own head.
 */
const packetStyle = (route: Rung): Vars => {
  const comet = Math.min(38, Math.max(24, route.len * 0.16));
  return {
    "--vw-len": route.len,
    strokeDasharray: `${comet.toFixed(1)} ${(route.len - comet).toFixed(1)}`,
    animationDelay: `${route.delay}s`,
  };
};

const VirtualizedWorkstationFlow = () => (
  <>
    <svg
      viewBox="0 0 1600 344"
      fill="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {/* ---- engineering station ---- */}
      {RUNGS.map((rung) => (
        <React.Fragment key={rung.d}>
          <path className="vw-rung vw-glow" d={rung.d} style={packetStyle(rung)} />
          <path className="vw-rung vw-core" d={rung.d} style={packetStyle(rung)} />
        </React.Fragment>
      ))}
      {CONTACTS.map((c) => (
        <path
          key={`${c.x0}-${c.y}`}
          className="vw-spark"
          d={`M${c.x0} ${c.y} H${c.x1}`}
          style={{ animationDelay: `${c.t}s` }}
        />
      ))}
      {COILS.map((c) => (
        <ellipse
          key={c.cy}
          className="vw-coil"
          cx={c.cx}
          cy={c.cy}
          rx={11}
          ry={7.5}
          style={{ animationDelay: `${c.t}s` }}
        />
      ))}
      {BLOCKS.map((b) => (
        <rect
          key={b.x}
          className="vw-block"
          x={b.x}
          y={b.y}
          width={b.w}
          height={b.h}
          rx={1}
          style={{ animationDelay: `${b.t}s` }}
        />
      ))}
      {TAGS.map((tag) => (
        <rect
          key={`${tag.x}-${tag.y}`}
          className="vw-tag"
          x={tag.x}
          y={tag.y}
          width={tag.w}
          height={tag.h}
          style={{ animationDelay: `${tag.t}s` }}
        />
      ))}

      {/* ---- HMI / SCADA ---- */}
      {HMI_ROUTES.map((route) => (
        <React.Fragment key={route.d}>
          <path className="vw-hmi vw-glow" d={route.d} style={packetStyle(route)} />
          <path className="vw-hmi vw-core" d={route.d} style={packetStyle(route)} />
        </React.Fragment>
      ))}
      {HMI_ROWS.map((row) =>
        row.links.map((d) => (
          <path key={d} className="vw-link" d={d} style={{ animationDelay: `${row.t}s` }} />
        ))
      )}
      {HMI_ROWS.map((row) =>
        row.dots.map(([cx, cy], i) => (
          <circle
            key={`${cx}-${cy}`}
            className="vw-dot"
            cx={cx}
            cy={cy}
            r={4.2}
            style={{ animationDelay: `${row.t + i * 0.06}s` }}
          />
        ))
      )}
      {ALARM_BARS.map((bar) => (
        <rect
          key={bar.y}
          className="vw-alarm"
          x={bar.x}
          y={bar.y}
          width={bar.w}
          height={bar.h}
          style={{ animationDelay: bar.delay }}
        />
      ))}
      {/* Status lamp beside the right-hand device. */}
      <circle className="vw-lamp" cx={920.5} cy={102} r={5.5} />
      {/* The log panel refreshes: a highlight sweeps down its rows. */}
      <rect className="vw-logscan" x={769} y={87} width={62} height={4} />

      {/* ---- historian ---- */}
      <path
        className="vw-trace vw-trace-cyan"
        d={CYAN_TRACE}
        style={{ "--vw-len": CYAN_TRACE_LEN } as Vars}
      />
      <path
        className="vw-trace vw-trace-red"
        d={RED_TRACE}
        style={{ "--vw-len": RED_TRACE_LEN } as Vars}
      />
      <circle
        className="vw-head vw-head-cyan"
        cx={0}
        cy={0}
        r={3.2}
        style={{ offsetPath: `path("${CYAN_TRACE}")` } as Vars}
      />
      <circle
        className="vw-head vw-head-red"
        cx={0}
        cy={0}
        r={3.2}
        style={{ offsetPath: `path("${RED_TRACE}")` } as Vars}
      />
      {RECORD_ROWS.map((y, i) => (
        <rect
          key={y}
          className="vw-record"
          x={1141}
          y={y}
          width={78}
          height={11}
          rx={2}
          style={{ animationDelay: `${(i * 0.16).toFixed(2)}s` }}
        />
      ))}
    </svg>

    <style jsx>{`
      /* ---------------------------------------------------------- screen 1 */
      /* One scan of the program: rung 1 and its branch, then rung 2, then rung
         3, then a beat of quiet before the controller starts over. */
      .vw-rung {
        stroke-linecap: round;
        animation: vwRun 5.4s linear infinite both;
      }
      @keyframes vwRun {
        0% {
          stroke-dashoffset: var(--vw-len);
          opacity: 0;
        }
        3% {
          opacity: 1;
        }
        17% {
          stroke-dashoffset: 0;
          opacity: 1;
        }
        21%,
        100% {
          stroke-dashoffset: 0;
          opacity: 0;
        }
      }
      /* Widening the same dash is cheaper than a blur filter and survives the
         card being scaled down on mobile. */
      .vw-glow {
        stroke: #2dbfc8;
        stroke-width: 8;
        opacity: 0.16;
      }
      .vw-core {
        stroke: #7fe9f5;
        stroke-width: 2.2;
      }

      /* A contact closing: the gap between its two bars is bridged for a beat. */
      .vw-spark {
        stroke: #dffbff;
        stroke-width: 2.6;
        stroke-linecap: round;
        animation: vwFlash 5.4s ease-out infinite both;
      }
      @keyframes vwFlash {
        0% {
          opacity: 0;
        }
        3% {
          opacity: 1;
        }
        13%,
        100% {
          opacity: 0;
        }
      }

      .vw-coil {
        stroke: #7fe9f5;
        stroke-width: 2;
        transform-box: fill-box;
        transform-origin: center;
        animation: vwCoil 5.4s ease-out infinite both;
      }
      @keyframes vwCoil {
        0% {
          opacity: 0;
          transform: scale(0.6);
        }
        4% {
          opacity: 0.95;
          transform: scale(1);
        }
        16%,
        100% {
          opacity: 0;
          transform: scale(1.25);
        }
      }

      .vw-block {
        stroke: #7fe9f5;
        stroke-width: 1.6;
        fill: #7fe9f5;
        animation: vwBlock 5.4s ease-out infinite both;
      }
      @keyframes vwBlock {
        0% {
          opacity: 0;
          fill-opacity: 0;
        }
        4% {
          opacity: 0.85;
          fill-opacity: 0.14;
        }
        16%,
        100% {
          opacity: 0;
          fill-opacity: 0;
        }
      }

      /* The tag label brightens over the symbol it names. */
      .vw-tag {
        fill: #d8fbff;
        animation: vwTag 5.4s ease-out infinite both;
      }
      @keyframes vwTag {
        0% {
          opacity: 0;
        }
        4% {
          opacity: 0.85;
        }
        18%,
        100% {
          opacity: 0;
        }
      }

      /* ---------------------------------------------------------- screen 2 */
      .vw-hmi {
        stroke-linecap: round;
        animation: vwRun 4.4s linear infinite both;
      }

      /* The answer travelling back up the red link, row by row. */
      .vw-link {
        stroke: #ff5c74;
        stroke-width: 2.4;
        stroke-linecap: round;
        animation: vwLink 4.4s ease-out infinite both;
      }
      @keyframes vwLink {
        0% {
          opacity: 0;
        }
        4% {
          opacity: 0.95;
        }
        20%,
        100% {
          opacity: 0;
        }
      }

      .vw-dot {
        fill: #ff5c74;
        transform-box: fill-box;
        transform-origin: center;
        animation: vwDot 4.4s ease-out infinite both;
      }
      @keyframes vwDot {
        0% {
          opacity: 0;
          transform: scale(0.5);
        }
        5% {
          opacity: 1;
          transform: scale(1.35);
        }
        22%,
        100% {
          opacity: 0;
          transform: scale(0.9);
        }
      }

      .vw-alarm {
        fill: #ff3d5c;
        animation: vwAlarm 1.7s ease-in-out infinite;
      }
      @keyframes vwAlarm {
        0%,
        100% {
          opacity: 0;
        }
        50% {
          opacity: 0.7;
        }
      }

      .vw-lamp {
        fill: #ff6076;
        animation: vwLamp 2.4s ease-in-out infinite;
      }
      @keyframes vwLamp {
        0%,
        100% {
          opacity: 0;
        }
        45% {
          opacity: 0.55;
        }
      }

      /* Log panel: rows x768..832, y86..123 in the artwork. */
      .vw-logscan {
        fill: #eaf6f8;
        opacity: 0;
        animation: vwLogScan 3.6s linear infinite;
      }
      @keyframes vwLogScan {
        0% {
          transform: translateY(0);
          opacity: 0;
        }
        10% {
          opacity: 0.3;
        }
        70% {
          opacity: 0.3;
        }
        80%,
        100% {
          transform: translateY(32px);
          opacity: 0;
        }
      }

      /* ---------------------------------------------------------- screen 3 */
      /* Both traces re-draw over the painted ones, so the chart replays instead
         of gaining a second line. */
      .vw-trace {
        fill: none;
        stroke-width: 2.4;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-dasharray: var(--vw-len);
        animation: vwDraw 5.6s linear infinite both;
      }
      @keyframes vwDraw {
        0% {
          stroke-dashoffset: var(--vw-len);
          opacity: 0.9;
        }
        60% {
          stroke-dashoffset: 0;
          opacity: 0.9;
        }
        78% {
          stroke-dashoffset: 0;
          opacity: 0.9;
        }
        92%,
        100% {
          stroke-dashoffset: 0;
          opacity: 0;
        }
      }
      .vw-trace-cyan {
        stroke: #7fe9f5;
      }
      .vw-trace-red {
        stroke: #ff5c74;
      }

      /* The head rides the same path on the same clock, so it sits exactly on
         the end of the line being drawn. transform-box fill-box is what lets
         offset-anchor resolve to the marker's own centre inside an SVG. */
      .vw-head {
        transform-box: fill-box;
        transform-origin: center;
        animation: vwHead 5.6s linear infinite both;
      }
      @keyframes vwHead {
        0% {
          offset-distance: 0%;
          opacity: 0;
        }
        4% {
          opacity: 1;
        }
        60% {
          offset-distance: 100%;
          opacity: 1;
        }
        70%,
        100% {
          offset-distance: 100%;
          opacity: 0;
        }
      }
      .vw-head-cyan {
        fill: #d6fbff;
      }
      .vw-head-red {
        fill: #ffd2da;
      }

      /* Records loading down the list. */
      .vw-record {
        fill: #ffffff;
        animation: vwRecord 2.9s ease-in-out infinite both;
      }
      @keyframes vwRecord {
        0% {
          opacity: 0;
        }
        8% {
          opacity: 0.42;
        }
        30%,
        100% {
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        /* No motion means no overlay — leave the three screens as drawn. */
        .vw-rung,
        .vw-spark,
        .vw-coil,
        .vw-block,
        .vw-tag,
        .vw-hmi,
        .vw-link,
        .vw-dot,
        .vw-alarm,
        .vw-lamp,
        .vw-logscan,
        .vw-trace,
        .vw-head,
        .vw-record {
          animation: none;
          display: none;
        }
      }
    `}</style>
  </>
);

export default VirtualizedWorkstationFlow;
